/**
 * 创建页顶部**来源条**(波1,2026-07-25)—— 「我从哪来、它替我定了什么、怎么换」。
 *
 * ═══ 为什么要有这条 ═══════════════════════════════════════════════════════════════
 * 创建页是五来源共用的一个外壳(自由 / 目的卡 / 追爆蓝图 / 模板 / 海报转视频)。
 * 波1 之前:蓝图有条(`ExplosiveBlueprintBar`)、模板有半条(封面+名字)、海报只在页头副标
 * 写一句、目的卡**什么都没有** —— 点「产品带货」卡进来的人,看到的页面与直接访问
 * `/ai-video/create` 逐字一致,只有一个 chip 被默默选中了。用户无法判断那张卡做了什么。
 * (同一个病在海报侧已有定论:BP-04「真问题是缺反馈,不是缺预填」,修法是把来源说出来。)
 *
 * 所以这里把**五来源收进同一个挂载点、同一套形制**:
 *     [图标/缩略图] [来源类别 tag] [这一份具体是什么] [它替你定了什么] [换/清除]
 * 形制取自设计稿 `_design-mock/dept-video-create.html` 的 `.srcbar`(icon·tag·title·meta·actions);
 * ★2026-07-25 该 strip 已抽成共享 `shared/ui/SourceStrip`(海报舱 V1 用的是同一份形制),
 *   本文件只剩「视频五来源各自说什么」这一份领域数据。
 *
 * ═══ ★ 追爆蓝图:细条 → 蓝图工作卡(BPWB-1,2026-09-03)═══════════════════════════
 * 追爆这条从来装不进 strip 两行(复刻档切换 + 无场景层诚实标注)。0903 起它反向升级成
 * `BlueprintHeroCard`(entities/explosive,五目的地统一形制):原片播放 + 承诺句 +
 * 仿拍镜头结构轨/学套路节拍轨,档位与诚实标注原样搬进卡内。单一挂载点不变 ——
 * 变的只是这条来源终于长成它该有的信息量(用户反馈「点过去还在我们首页」的正解)。
 * 原 `ExplosiveBlueprintBar` 退役已删,行为断言移入 VideoSourceBar.test.tsx。
 */
import { Film, ImageIcon, PencilLine, RefreshCcw } from "lucide-react";

import type { BriefPurposeDef } from "@/entities/scene-card/model/briefPurpose";
import { Button } from "@/shared/ui/button";
import { SourceStrip } from "@/shared/ui/SourceStrip";
import { FREE_FLOW_SOURCE_META } from "../../model/freeFlowHints";

import type { FormulaPayload } from "@/entities/explosive";
import {
  BlueprintHeroCard,
  BlueprintShotRail,
  parseBeats,
  planShotRail,
} from "@/entities/explosive";

import type { PurposeNotice } from "../../model/purposeNotice";

/** 复刻档(BPWB-1:原 ExplosiveBlueprintBar 退役后,类型落户到唯一消费点)。 */
export type FidelityMode = "learn" | "copy";

/** 各来源自带展示字段 + 自己的动作(同一个挂载点,按 kind 分派)。 */
export type VideoSourceBarData =
  /**
   * ★2026-08-06 重排:目的的交互出口**已不在这条**。
   *
   * 两态收敛批 D1 曾把目的收敛成「来源条原地 popover」,那一轮解决的是「目的出现三次、
   * 来源条指着自己的复制品说去那儿改」。但收敛到 popover 之后又暴露出新问题(诊断 P7):
   * 目的决定 8 套叙事弧中的哪一套,它的入口却是一颗 `size="sm" variant="ghost"` 小按钮,
   * **视觉权重低于旁边的「保存草稿」**,不选还静默兜底「产品带货」。
   * D2 拍板:提成备料台①常驻单行 chip(`VideoPurposeRow`)。
   * 来源条回到它本来的职责 ——「我从哪来」,不再兼任最重要那个输入的入口。
   */
  /**
   * `notice`(2026-08-02)= 「这次实际按什么目的编排 + 是谁定的」,由 `purposeNotice()` 单点产出。
   *
   * ★取代了原来的 `fallbackPurposeLabel`。那个字段的注释写着:
   *   「不是『我们推断出来的目的』—— 引擎只按 purpose 字符串做关键词匹配,**不看文案**」
   * 这在当时完全正确;但 ai 侧 `intent_infer`(2026-08-02)之后引擎**真的读文案了**,
   * 那句话就从「保守」变成了「说少了」:AI 判出了「公司介绍」,界面还写「按产品带货编排」。
   * 撒谎的方向反了,但仍然是撒谎。四态措辞与依据全在 `purposeNotice`,这里只负责渲染。
   */
  | {
      kind: "free";
      notice?: PurposeNotice;
    }
  /**
   * `formulaName` = 用户在「选拍法」里点的那张(波2)。缺省 = 没选 = 引擎自动。
   * ★这条 meta 原文写死「拍法由 AI 按目的自动选」—— 波2 之后那句会变成谎:用户明明选了一张。
   *   来源条是「它替你定了什么」的唯一出口,定的东西变了这里必须跟着变。
   * `storyEngine` = 该目的自动开故事引擎(剧情微故事;Q3 拍板:自动行为要在来源条可见)。
   */
  | {
      kind: "purpose";
      purpose: BriefPurposeDef;
      formulaName?: string | null;
      storyEngine?: boolean;
      /**
       * ★VID-CMP-06(2026-08-11):手选档同样要有出处与 AI 分歧留痕。
       * `purposeNotice` 的 user 分支早就产出「按「X」编排」+ 分歧 detail 并有单测,
       * 但这条形态一直没有 notice 字段 —— 措辞造好了,渲染层断线,用户拿到拧的片子无从归因
       * (400 电话真单:选「品牌宣传」贴业务百科,机器全程没吭声)。与 free 形态同一份产出。
       */
      notice?: PurposeNotice;
    }
  | {
      kind: "template";
      name: string;
      description?: string | null;
      coverUrl?: string | null;
      onChange: () => void;
    }
  | { kind: "poster"; title: string | null; bootstrapping: boolean; thumbUrl?: string | null }
  | {
      kind: "explosive";
      formulaName: string;
      sourcePlatform?: string | null;
      fidelityMode: FidelityMode;
      hasSceneCards: boolean;
      /** EXP-RAW-1:蓝图留了原片 → 仿拍档参考原片画面(r2v);缺省/false 维持纯场景卡文案。 */
      hasRawVideo?: boolean;
      /** BPWB-1:工作卡播原片要现签直链;存量草稿缺省 → 不渲染播放位。 */
      blueprintId?: number | null;
      /** BPWB-1:幕二收紧凑态(播放位/结构轨收起,只留头行+档位)。 */
      compact?: boolean;
      /** 蓝图 payload —— 「这次带给 AI 的原文」面板 + 仿拍镜头结构轨都读它。 */
      payload?: FormulaPayload | null;
      onFidelityChange: (m: FidelityMode) => void;
      onSwap: () => void;
      onClear: () => void;
    };

interface Props {
  data: VideoSourceBarData;
  /** 编排/出图进行中:整条冻结(brief 已被服务端快照,这时候换来源没有意义)。 */
  disabled?: boolean;
}

export function VideoSourceBar({ data, disabled }: Props) {
  switch (data.kind) {
    case "explosive": {
      // BPWB-1(2026-09-03):细条升级成蓝图工作卡 —— 原片播放 + 承诺句 + 镜头结构轨。
      // 复刻档切换与无场景层诚实标注(原 ExplosiveBlueprintBar 的两样命根)原样搬进卡内。
      const copyMode = data.fidelityMode === "copy";
      const plan = copyMode && !data.compact ? planShotRail(data.payload?.sceneCards) : null;
      const beats = !copyMode && !data.compact ? parseBeats(data.payload?.beats15Raw) : [];
      const pledge = !data.hasSceneCards ? undefined : copyMode ? (
        data.hasRawVideo ? (
          <>
            运镜、构图、节奏<b>逐镜照这条原片拍</b>;画面里的产品换成你的,口播和字幕按你的产品重写。
            原片中的品牌、文字、人脸不会出现。
          </>
        ) : (
          <>
            结构+场景双保真:画面照参考片拍法铺,口播按你的产品重写。
            这条蓝图没留原片,画面按场景卡文字想象 —— 重新解析同链接可补上真画面锚。
          </>
        )
      ) : (
        <>只学结构骨架,画面由 AI 自由创作;口播和字幕按你的产品写。</>
      );
      return (
        <div className="space-y-2">
          <BlueprintHeroCard
            formulaName={data.formulaName}
            sourcePlatform={data.sourcePlatform}
            blueprintId={data.blueprintId}
            hasRawVideo={data.hasRawVideo}
            compact={data.compact}
            disabled={disabled}
            onSwap={data.onSwap}
            onClear={data.onClear}
            pledge={pledge}
            structure={
              plan && plan.items.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                    <b className="text-foreground">
                      镜头结构 · 共 {plan.takeTotal} 镜 · 成片约 {plan.totalSec}s
                    </b>
                    <span>时长跟原片走,不用选</span>
                  </div>
                  <BlueprintShotRail plan={plan} />
                </div>
              ) : beats.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="text-xs text-muted-foreground">
                    <b className="text-foreground">套路节拍</b> —— 只借骨架,时长比例风格照下面选的来
                  </div>
                  <div data-testid="blueprint-beats-rail" className="flex flex-wrap gap-1">
                    {beats.map((bt, i) => (
                      <span
                        key={i}
                        className="rounded-md border border-border/60 bg-card px-2 py-1 text-2xs text-muted-foreground"
                      >
                        {bt.start != null && bt.end != null && (
                          <b className="mr-1 font-mono text-[10px] text-primary/80">
                            {bt.start}–{bt.end}s
                          </b>
                        )}
                        {bt.label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : undefined
            }
          >
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-full border border-border bg-card p-0.5">
                <Button
                  type="button"
                  size="sm"
                  variant={copyMode ? "default" : "ghost"}
                  className="h-6 rounded-full px-3 text-xs"
                  aria-pressed={copyMode}
                  disabled={disabled || !data.hasSceneCards}
                  title={
                    !data.hasSceneCards
                      ? "此蓝图无场景层,仅支持学套路;重新解析同链接可获得"
                      : data.hasRawVideo
                        ? "参考爆款原片画面仿拍,说什么按你的产品来"
                        : "他的拍法照着来,说什么按你的产品来"
                  }
                  onClick={() => data.onFidelityChange("copy")}
                >
                  照原片仿拍
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={!copyMode ? "default" : "ghost"}
                  className="h-6 rounded-full px-3 text-xs"
                  aria-pressed={!copyMode}
                  disabled={disabled}
                  onClick={() => data.onFidelityChange("learn")}
                >
                  只学套路
                </Button>
              </div>
              {!data.hasSceneCards && (
                <span className="text-xs text-muted-foreground">
                  此蓝图无场景层(解析于场景层上线前),仅支持学套路;重新解析同链接可获得仿拍档
                </span>
              )}
            </div>
            {/* 真人出镜:引擎肖像合规会拒含真人脸的参考视频(0902 真机定案)。解析期还没有
                「有真人」标记(挂账),先给静态明显提示 —— 用户拍板「给个明显提示就行」。 */}
            {copyMode && data.hasRawVideo && !data.compact && (
              <p
                data-testid="human-face-copy-hint"
                className="rounded-md border border-amber-400/40 bg-amber-500/5 px-2.5 py-1.5 text-xs leading-relaxed text-amber-700 dark:text-amber-400"
              >
                原片有真人出镜?引擎会拒绝仿拍出片(肖像合规,不扣费)——
                这类片建议切「只学套路」;想让你自己出镜,去真人出镜页按这条的套路写脚本拍
                (照原片仿拍不支持本人出镜)。
              </p>
            )}
          </BlueprintHeroCard>
          {/* 2026-09-12 B2:「这次带给 AI 的原文」(HandoffPreview)已从这条横幅移出,
              与「参考片在拍什么」合成创建页的「原片拆解」一条折叠 —— 首屏让给操作。 */}
        </div>
      );
    }

    case "purpose": {
      const { purpose } = data;
      // ★pending(还没编排/老草稿)不渲 notice:那句「没选目的也能出片」是给自由态的 ——
      //   这条 strip 的 title 就是用户选好的目的,再说「没选目的」等于来源条自己打自己。
      const notice = data.notice?.tone === "pending" ? undefined : data.notice;
      return (
        <SourceStrip
          domainAttr="data-video-source-strip"
          accent
          tag="视频目的"
          title={purpose.label}
          // 复述卡面那句「一句话用途」——单源,不另写一套文案(不会漂移)。
          // 剧情微故事 = 自动开故事引擎,必须在这里明说(Q3:自动行为要可见)。
          // ★主行带出处(VID-CMP-06):「按「X」编排」由 purposeNotice 单点产出,这里只渲染
          //   不复述措辞;AI 分歧走 detail 第二行 —— 挤进单行 truncate 的 meta 会被吃掉尾巴,
          //   而分歧留痕的价值恰恰在被完整读到。
          // ★时长紧跟出处排第二(2026-08-20):这条 meta 是**单行 truncate**,排末尾会被吃掉尾巴 ——
          //   而「这次是多少秒、是谁定的」正是用户最想知道、也最容易误会的一格
          //   (用户反馈「不选时长都会降级到 15s」,实为兜底目的「产品带货」的基准秒数就是 15,
          //   而 ai 侧早就把 durationReason 回传了,只是**只有自由创作那条分支渲出来**)。
          //   pending(还没编排)时 notice 已被上面置空 → 这里自然不显示,不会硬造一个秒数。
          meta={[
            notice?.text,
            // ★用 data.notice 而不是上面那个 pending 置空后的 notice:pending 只该压住
            //   「没选目的也能出片」那句(它与 title 自打脸),而 durationText 一旦有值就是
            //   **编排后的真秒数**,压住它等于把已经知道的事实藏起来。没编排时它本来就是空。
            data.notice?.durationText,
            `${purpose.usage} · ${
              data.formulaName ? `拍法「${data.formulaName}」` : "拍法由 AI 按目的自动选"
            }`,
            data.storyEngine ? "按故事弧编排·多镜锁同一主角" : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          detail={notice?.detail}
          icon={<purpose.Icon className="h-5 w-5" strokeWidth={1.8} />}
        />
      );
    }

    case "template":
      return (
        <SourceStrip
          domainAttr="data-video-source-strip"
          accent
          tag="模板视频"
          title={data.name}
          meta={data.description?.trim() || "已带入脚本与推荐参数,填空即可生成"}
          thumbUrl={data.coverUrl}
          icon={<Film className="h-5 w-5" strokeWidth={1.8} />}
          action={
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1"
              disabled={disabled}
              onClick={data.onChange}
            >
              <RefreshCcw className="h-3 w-3" />
              换模板
            </Button>
          }
        />
      );

    case "poster":
      return (
        <SourceStrip
          domainAttr="data-video-source-strip"
          accent
          tag="海报转视频"
          title={data.bootstrapping ? "正在从海报装配参数…" : `海报「${data.title ?? "…"}」`}
          meta={
            data.bootstrapping
              ? "读取海报提示词与画幅"
              : "画面锚定这张海报;想讲故事可在下方开故事引擎"
          }
          thumbUrl={data.thumbUrl}
          icon={<ImageIcon className="h-5 w-5" strokeWidth={1.8} />}
        />
      );

    case "free":
      // ★分流收口配套(2026-09-14 老板点名):自由创作=直发单镜,不再走编排 ——
      //   purposeNotice 的 pending 文案「AI 会按你的文案判该怎么拍」在**还会编排的流**里是
      //   真话(模板/老草稿照用),在这条流里成了假话,所以这里不吃 notice,写死直发事实。
      // ★0812 反馈 / Z9d 0926:自由创作精简页没有配音、字幕、语速这几格,用户找不到就以为丢了。
      //   这里把「它们在哪」说出来 —— 走目的流才有(选一个用途)。
      return (
        <SourceStrip
          domainAttr="data-video-source-strip"
          tag="自由创作"
          title="自己写,直接出片"
          meta={FREE_FLOW_SOURCE_META}
          icon={<PencilLine className="h-5 w-5" strokeWidth={1.8} />}
        />
      );
  }
}
